/**
 * Asset Detail Page
 * Display comprehensive Asset user information
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Skeleton,
  TablePagination,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  InputBase,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMessage } from '@/contexts/MessageContext';
import {
  CheckCircle as ApproveIcon,
  Block as SuspendIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { UserService } from '../../../services/user/UserService';
import type { UserDetail, UserWarning } from '../../../types/user';
import { AssetInfoCard } from './components/AssetInfoCard';
import { MissionsTab } from './components/tabs/MissionsTab';
import { PaymentsTab } from './components/tabs/PaymentsTab';
import { PreferencesTab } from './components/tabs/PreferencesTab';
import { IssueWarningDialog } from './components/IssueWarningDialog';
import {
  PrimaryButton,
} from '../../../components/ui';
import { formatDateTime } from '@/utils/dateUtils';

type TabValue = 'missions' | 'payments' | 'preferences' | 'warnings';

const getStatusColor = (status: string, theme: any): string => {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'disabled':
      return theme.palette.error.main;
    case 'pending':
      return theme.palette.warning.main;
    case 'suspended':
      return theme.palette.grey[500];
    default:
      return theme.palette.text.secondary;
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'disabled':
      return 'Disabled';
    case 'pending':
      return 'Pending';
    case 'suspended':
      return 'Suspended';
    default:
      return status;
  }
};

const AssetDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { showError } = useMessage();
  const [assetDetail, setAssetDetail] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('missions');

  // Suspension dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendUntil, setSuspendUntil] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [isSuspending, setIsSuspending] = useState(false);

  // Activation dialog state
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateReason, setActivateReason] = useState('');
  const [isActivating, setIsActivating] = useState(false);

  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  // Warnings pagination state
  const [warnings, setWarnings] = useState<UserWarning[]>([]);
  const [totalWarnings, setTotalWarnings] = useState(0);
  const [warningPage, setWarningPage] = useState(1);
  const [warningRowsPerPage, setWarningRowsPerPage] = useState(5);
  const [warningKeyword, setWarningKeyword] = useState('');
  const [isWarningsLoading, setIsWarningsLoading] = useState(false);

  const fetchAssetDetail = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const detail = await UserService.getUserDetail(id);
      // Ensure this is an Asset user
      if (detail && detail.userType === 'asset') {
        setAssetDetail(detail);
      } else {
        setAssetDetail(null);
      }
    } catch (error) {
      console.error('Failed to fetch asset detail:', error);
      setAssetDetail(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWarnings = async () => {
    if (!id) return;
    try {
      setIsWarningsLoading(true);
      const res = await UserService.getWarningHistory(id, warningKeyword, warningPage, warningRowsPerPage);
      setWarnings(res.data);
      setTotalWarnings(res.total);
    } catch (error) {
      console.error('Failed to fetch asset warnings:', error);
      showError('Failed to load warning history.');
    } finally {
      setIsWarningsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssetDetail();
  }, [id, showError]);

  useEffect(() => {
    if (id) {
      fetchWarnings();
    }
  }, [id, warningPage, warningRowsPerPage, warningKeyword]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleSuspendUser = () => {
    setSuspendDialogOpen(true);
  };

  const handleSuspendDialogClose = () => {
    setSuspendDialogOpen(false);
    setSuspendUntil('');
    setSuspendReason('');
  };

  const handleSuspendConfirm = async () => {
    if (!id || !assetDetail || !suspendUntil || !suspendReason) return;

    try {
      setIsSuspending(true);
      const suspendUntilISO = new Date(suspendUntil).toISOString();
      await UserService.suspendUser(id, suspendUntilISO, suspendReason);

      // Refresh user detail
      const detail = await UserService.getUserDetail(id);
      if (detail && detail.userType === 'asset') {
        setAssetDetail(detail);
      }

      handleSuspendDialogClose();
    } catch (error: any) {
      console.error('Failed to suspend user:', error);
      showError(error.message || 'Failed to suspend user.');
    } finally {
      setIsSuspending(false);
    }
  };

  const handleActivateUser = () => {
    setActivateDialogOpen(true);
  };

  const handleActivateDialogClose = () => {
    setActivateDialogOpen(false);
    setActivateReason('');
  };

  const handleActivateConfirm = async () => {
    if (!id || !assetDetail || !activateReason) return;

    try {
      setIsActivating(true);
      await UserService.activateUser(id, activateReason);

      // Refresh user detail
      const detail = await UserService.getUserDetail(id);
      if (detail && detail.userType === 'asset') {
        setAssetDetail(detail);
      }

      handleActivateDialogClose();
    } catch (error: any) {
      console.error('Failed to activate user:', error);
      showError(error.message || 'Failed to activate user.');
    } finally {
      setIsActivating(false);
    }
  };

  const handleIssueWarning = () => {
    setWarningDialogOpen(true);
  };

  const handleIssueWarningConfirm = async (reason: string) => {
    if (!id) return;

    try {
      await UserService.issueWarning(id, reason);

      // Refresh user detail
      const detail = await UserService.getUserDetail(id);
      if (detail && detail.userType === 'asset') {
        setAssetDetail(detail);
      }

      // Refresh warnings
      fetchWarnings();

      setWarningDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to issue warning:', error);
      showError(error.message || 'Failed to issue warning.');
      throw error;
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!assetDetail) {
    return (
      <MainLayout>
        <EmptyState
          title="Asset Not Found"
          message="The Asset you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  return (
    <>
      <MainLayout>
        {/* Page Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: theme.spacing(3),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              Asset: {assetDetail.name}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                padding: theme.spacing(0.5, 1.5),
                borderRadius: theme.shapes.borderRadius.lg,
                backgroundColor: getStatusColor(assetDetail.status, theme),
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {getStatusLabel(assetDetail.status)}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
            <PrimaryButton
              startIcon={<WarningIcon />}
              onClick={handleIssueWarning}
              sx={{
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.common.black,
                padding: theme.spacing(1, 2),
                '&:hover': {
                  backgroundColor: theme.palette.warning.dark,
                },
              }}
            >
              Issue Warning
            </PrimaryButton>
            {assetDetail.status === 'suspended' ? (
              <PrimaryButton
                startIcon={<ApproveIcon />}
                onClick={handleActivateUser}
                sx={{
                  backgroundColor: theme.palette.success.main,
                  padding: theme.spacing(1, 2),
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  },
                }}
              >
                Activate User
              </PrimaryButton>
            ) : (
              <PrimaryButton
                startIcon={<SuspendIcon />}
                onClick={handleSuspendUser}
                sx={{
                  backgroundColor: theme.palette.error.main,
                  padding: theme.spacing(1, 2),
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                  },
                }}
              >
                Suspend User
              </PrimaryButton>
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left Column - Asset Profile */}
          <Grid item xs={12} md={4}>
            <AssetInfoCard assetDetail={assetDetail} />
          </Grid>

          {/* Right Column - Tabs */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ padding: 0 }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: theme.customColors.tableBorder }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        minHeight: 48,
                      },
                      '& .Mui-selected': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Tab
                      icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Missions"
                      value="missions"
                    />
                    <Tab
                      icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Payments"
                      value="payments"
                    />
                    <Tab
                      icon={<SettingsIcon sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Preferences"
                      value="preferences"
                    />
                    <Tab
                      icon={<WarningIcon sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Warnings"
                      value="warnings"
                    />
                  </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ padding: theme.spacing(3) }}>
                  {activeTab === 'missions' && <MissionsTab userDetail={assetDetail} />}
                  {activeTab === 'payments' && <PaymentsTab assetDetail={assetDetail} />}
                  {activeTab === 'preferences' && <PreferencesTab assetDetail={assetDetail} />}
                  {activeTab === 'warnings' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                          }}
                        >
                          Warning History
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: theme.shapes.borderRadius.md,
                            backgroundColor: theme.customColors.searchBackground,
                            border: `1px solid ${theme.customColors.searchBorder}`,
                            width: 250,
                          }}
                        >
                          <Box
                            sx={{
                              padding: theme.spacing(0, 1),
                              height: '100%',
                              position: 'absolute',
                              pointerEvents: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                          </Box>
                          <InputBase
                            placeholder="Search warnings..."
                            value={warningKeyword}
                            onChange={(e) => {
                              setWarningKeyword(e.target.value);
                              setWarningPage(1);
                            }}
                            sx={{
                              color: theme.palette.text.primary,
                              width: '100%',
                              padding: theme.spacing(1, 1, 1, 4),
                              fontSize: '0.875rem',
                            }}
                          />
                        </Box>
                      </Box>

                      {isWarningsLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                        </Box>
                      ) : !warnings || warnings.length === 0 ? (
                        <EmptyState
                          title="No Warnings"
                          message="This user has no warning history."
                        />
                      ) : (
                        <>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Reason / Message
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Issued By
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Date
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {warnings.map((warning) => (
                                <TableRow key={warning.id}>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{warning.reason}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.secondary }}>{warning.issuedByName}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.secondary }}>{formatDateTime(warning.date)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {totalWarnings > 0 && (
                            <TablePagination
                              component="div"
                              count={totalWarnings}
                              page={warningPage - 1}
                              onPageChange={(_, newPage) => setWarningPage(newPage + 1)}
                              rowsPerPage={warningRowsPerPage}
                              onRowsPerPageChange={(e) => {
                                setWarningRowsPerPage(parseInt(e.target.value, 10));
                                setWarningPage(1);
                              }}
                              rowsPerPageOptions={[5, 10, 25]}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MainLayout>

      <IssueWarningDialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        onConfirm={handleIssueWarningConfirm}
        userName={assetDetail.name}
      />

      {/* Suspension Dialog */}
      <Dialog open={suspendDialogOpen} onClose={handleSuspendDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please provide the suspension details for {assetDetail?.name}.
            </Typography>

            <TextField
              fullWidth
              label="Suspension Date & Time"
              type="datetime-local"
              value={suspendUntil}
              onChange={(e) => setSuspendUntil(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Reason for Suspension"
              multiline
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Please provide a reason for the suspension..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuspendDialogClose} disabled={isSuspending}>
            Cancel
          </Button>
          <Button
            onClick={handleSuspendConfirm}
            variant="contained"
            color="error"
            disabled={!suspendUntil || !suspendReason || isSuspending}
          >
            {isSuspending ? 'Suspending...' : 'Suspend User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activation Dialog */}
      <Dialog open={activateDialogOpen} onClose={handleActivateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Activate User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please provide a reason for activating {assetDetail?.name}.
            </Typography>

            <TextField
              fullWidth
              label="Reason for Activation"
              multiline
              rows={3}
              value={activateReason}
              onChange={(e) => setActivateReason(e.target.value)}
              placeholder="Please provide a reason for the activation..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActivateDialogClose} disabled={isActivating}>
            Cancel
          </Button>
          <Button
            onClick={handleActivateConfirm}
            variant="contained"
            color="success"
            disabled={!activateReason || isActivating}
          >
            {isActivating ? 'Activating...' : 'Activate User'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetDetailPage;

